/**
 * AMD File Parser/Writer
 *
 * .amd format structure:
 * - Header: 256 bytes containing "MAPSIZEX = N MAPSIZEY = M TILESIZE = 10"
 * - Tile data: width * height * 10 bytes
 *
 * Per tile (10 bytes):
 *   - bytes 0-1: tileSprite (uint16 LE)
 *   - bytes 2-3: tileSpriteFrame (uint16 LE)
 *   - bytes 4-5: objectSprite (uint16 LE)
 *   - bytes 6-7: objectSpriteFrame (uint16 LE)
 *   - byte 8: flags
 *     - bit 7 (0x80): blocked
 *     - bit 6 (0x40): teleport
 *     - bit 5 (0x20): farming
 *     - bit 4 (0x10): buildable
 *   - byte 9: unused by the game, but not zero in stock maps - preserved as-is
 *
 * Loading then saving an unedited map must produce a byte-identical file.
 */

import { MapDocument, TileData, TileFlags, MAX_MAP_SIZE } from './types';

const HEADER_SIZE = 256;
const TILE_DATA_SIZE = 10;

/**
 * Parse AMD header to extract map dimensions
 */
function parseHeader(header: string): { width: number; height: number; tileSize: number | null } | null {
    // Parse format: "MAPSIZEX =  250 MAPSIZEY =  250 TILESIZE = 10"
    const sizeXMatch = header.match(/MAPSIZEX\s*=\s*(\d+)/);
    const sizeYMatch = header.match(/MAPSIZEY\s*=\s*(\d+)/);
    const tileSizeMatch = header.match(/TILESIZE\s*=\s*(\d+)/);

    if (!sizeXMatch || !sizeYMatch) {
        return null;
    }

    return {
        width: parseInt(sizeXMatch[1], 10),
        height: parseInt(sizeYMatch[1], 10),
        tileSize: tileSizeMatch ? parseInt(tileSizeMatch[1], 10) : null,
    };
}

/**
 * Parse a tile from binary data
 */
function parseTile(view: DataView, offset: number): TileData {
    const flags = view.getUint8(offset + 8);

    return {
        tileSprite: view.getUint16(offset, true),
        tileSpriteFrame: view.getUint16(offset + 2, true),
        objectSprite: view.getUint16(offset + 4, true),
        objectSpriteFrame: view.getUint16(offset + 6, true),
        blocked: (flags & TileFlags.BLOCKED) !== 0,
        teleport: (flags & TileFlags.TELEPORT) !== 0,
        farming: (flags & TileFlags.FARMING) !== 0,
        buildable: (flags & TileFlags.BUILDABLE) !== 0,
        reserved: view.getUint8(offset + 9),
    };
}

/**
 * Serialize a tile to binary data
 */
function serializeTile(tile: TileData, view: DataView, offset: number): void {
    view.setUint16(offset, tile.tileSprite, true);
    view.setUint16(offset + 2, tile.tileSpriteFrame, true);
    view.setUint16(offset + 4, tile.objectSprite, true);
    view.setUint16(offset + 6, tile.objectSpriteFrame, true);

    let flags = 0;
    if (tile.blocked) flags |= TileFlags.BLOCKED;
    if (tile.teleport) flags |= TileFlags.TELEPORT;
    if (tile.farming) flags |= TileFlags.FARMING;
    if (tile.buildable) flags |= TileFlags.BUILDABLE;

    view.setUint8(offset + 8, flags);
    view.setUint8(offset + 9, tile.reserved);
}

/**
 * Parse an AMD file from an ArrayBuffer
 */
export function parseAmd(buffer: ArrayBuffer, filename?: string): MapDocument {
    if (buffer.byteLength < HEADER_SIZE) {
        throw new Error(`Invalid AMD file: ${buffer.byteLength} bytes is smaller than the ${HEADER_SIZE} byte header`);
    }

    const view = new DataView(buffer);

    // Read header (latin1 so every byte decodes, the header itself is ASCII)
    const headerBytes = new Uint8Array(buffer, 0, HEADER_SIZE);
    const header = new TextDecoder('latin1').decode(headerBytes);

    const dimensions = parseHeader(header);
    if (!dimensions) {
        throw new Error('Invalid AMD file: could not parse header');
    }

    const { width, height, tileSize } = dimensions;

    if (tileSize !== null && tileSize !== TILE_DATA_SIZE) {
        throw new Error(`Unsupported AMD file: TILESIZE is ${tileSize}, expected ${TILE_DATA_SIZE}`);
    }

    if (width < 1 || height < 1 || width > MAX_MAP_SIZE || height > MAX_MAP_SIZE) {
        throw new Error(`Invalid AMD file: map size ${width}x${height} is outside 1-${MAX_MAP_SIZE}`);
    }

    // Validate file size
    const expectedSize = HEADER_SIZE + width * height * TILE_DATA_SIZE;
    if (buffer.byteLength < expectedSize) {
        throw new Error(
            `Invalid AMD file: expected ${expectedSize} bytes, got ${buffer.byteLength}`
        );
    }

    // Parse tiles (stored in row-major order: y outer, x inner)
    const tiles: TileData[][] = [];
    for (let x = 0; x < width; x++) {
        tiles[x] = new Array<TileData>(height);
    }

    let dataOffset = HEADER_SIZE;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            tiles[x][y] = parseTile(view, dataOffset);
            dataOffset += TILE_DATA_SIZE;
        }
    }

    return {
        width,
        height,
        tiles,
        filename,
        dirty: false,
        rawHeader: headerBytes.slice(),
    };
}

/**
 * Serialize a MapDocument to AMD format
 */
export function serializeAmd(map: MapDocument): ArrayBuffer {
    const totalSize = HEADER_SIZE + map.width * map.height * TILE_DATA_SIZE;
    const buffer = new ArrayBuffer(totalSize);
    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);

    // Write header - reuse the loaded one so spacing and padding survive
    if (map.rawHeader && map.rawHeader.length === HEADER_SIZE) {
        bytes.set(map.rawHeader, 0);
    } else {
        const headerStr = `MAPSIZEX =  ${map.width} MAPSIZEY =  ${map.height} TILESIZE = ${TILE_DATA_SIZE}`;
        bytes.set(new TextEncoder().encode(headerStr), 0);
        // Rest of header is zero-padded
    }

    // Write tiles (row-major order: y outer, x inner)
    let dataOffset = HEADER_SIZE;
    for (let y = 0; y < map.height; y++) {
        for (let x = 0; x < map.width; x++) {
            serializeTile(map.tiles[x][y], view, dataOffset);
            dataOffset += TILE_DATA_SIZE;
        }
    }

    return buffer;
}

/**
 * Load an AMD file from a File object
 */
export async function loadAmdFile(file: File): Promise<MapDocument> {
    const buffer = await file.arrayBuffer();
    return parseAmd(buffer, file.name);
}

/**
 * Download a MapDocument as an AMD file
 */
export function downloadAmd(map: MapDocument, filename?: string): void {
    const buffer = serializeAmd(map);
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename || map.filename || 'untitled.amd';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
